<template>
  <div class="home-page">
    <div class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">
          <div class="badge-icon">📹</div>
        </div>
        <h1 class="hero-title">双机位监考系统</h1>
        <p class="hero-subtitle">辅机位 · 环境监控</p>
      </div>
      <div class="status-bar">
        <span class="status-dot"></span>
        <span class="status-text">准备就绪</span>
      </div>
    </div>

    <div class="main-content">
      <!-- 监考说明 -->
      <div class="info-card">
        <div class="card-header">
          <div class="card-icon">📋</div>
          <h2 class="card-title">监考说明</h2>
        </div>
        <div class="card-body">
          <p class="card-description">
            辅机位用于全方位监控您的考试环境，确保考试的公平性和公正性。请按照指引正确摆放设备。
          </p>
        </div>
      </div>

      <!-- 手机摆放指导 -->
      <div class="placement-card">
        <div class="card-header">
          <div class="card-icon">📱</div>
          <h2 class="card-title">手机摆放指导</h2>
        </div>

        <div class="visual-guide">
          <div class="guide-diagram">
            <div class="diagram-item">
              <div class="diagram-icon">👤</div>
              <span class="diagram-label">考生</span>
            </div>
            <div class="diagram-arrow">↗️</div>
            <div class="diagram-item">
              <div class="diagram-icon">📱</div>
              <span class="diagram-label">手机 45°</span>
            </div>
          </div>
        </div>

        <div class="guide-text">
          <p class="guide-highlight">考生侧后方 45° 角</p>
          <p class="guide-detail">距离1-2米，高度与眼睛平齐</p>
        </div>

        <div class="setup-steps">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <div class="step-title">摆放位置</div>
              <div class="step-desc">距离考生1-2米，侧后方45°角</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <div class="step-title">高度要求</div>
              <div class="step-desc">与眼睛平齐或略高</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <div class="step-title">视野范围</div>
              <div class="step-desc">能看到面部、屏幕及周围环境</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">4</div>
            <div class="step-content">
              <div class="step-title">光线要求</div>
              <div class="step-desc">避免背光，确保光线充足</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 确认和开始 -->
      <div class="action-card">
        <label class="checkbox-label">
          <input
            v-model="confirmedPosition"
            type="checkbox"
            class="checkbox-input"
          >
          <span class="checkmark"></span>
          <span class="checkbox-text">我已将手机按要求放置在合适位置</span>
        </label>

        <button
          @click="startDetection"
          :disabled="!confirmedPosition"
          class="start-button"
        >
          <span class="button-text">开始设备检测</span>
          <span class="button-icon">→</span>
        </button>
      </div>

      <!-- 快捷入口 -->
      <div style="text-align: center; margin-top: 20px;">
        <button @click="goToPlay" style="background: none; border: none; color: var(--proctor-primary); text-decoration: underline; cursor: pointer;">
          进入监考画面观看页
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'HomeView',
  setup() {
    const router = useRouter()
    const confirmedPosition = ref(false)

    const startDetection = () => {
      if (confirmedPosition.value) {
        router.push('/detection')
      }
    }

    const goToPlay = () => {
      router.push('/play')
    }

    return {
      confirmedPosition,
      startDetection,
      goToPlay
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 0 var(--container-padding);
  padding-top: env(safe-area-inset-top); /* 为顶部安全区域留出空间 */
}

/* Hero Section - 现在支持深色模式 */
.hero-section {
  background: var(--bg-elevated);
  padding: 60px 20px 30px; /* 增加顶部内边距为60px */
  text-align: center;
  border-radius: 0 0 24px 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--proctor-primary), var(--proctor-secondary));
  border-radius: 50%;
  margin: 0 auto 20px;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.badge-icon {
  font-size: 2.5rem;
  color: white;
}

.hero-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 1rem;
  color: var(--text-tertiary);
  margin: 0;
}

.status-bar {
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--bg-primary);
  border-radius: 20px;
  border: 1px solid var(--border-light);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.status-dot {
  width: 8px;
  height: 8px;
  background: var(--success-500);
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s infinite;
}

.status-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* Main Content */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 卡片通用样式 */
.info-card,
.placement-card,
.action-card {
  background: var(--bg-elevated);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-secondary);
}

.card-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--proctor-primary), var(--proctor-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  flex-shrink: 0;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.card-body {
  padding: 20px 24px;
}

.card-description {
  color: var(--text-tertiary);
  line-height: 1.6;
  margin: 0;
}

/* 摆放指导 */
.visual-guide {
  padding: 20px 24px;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-light);
}

.guide-diagram {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  padding: 20px 0;
}

.diagram-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.diagram-icon {
  font-size: 2.5rem;
  background: var(--bg-primary);
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.diagram-label {
  font-size: 0.85rem;
  color: var(--text-tertiary);
  font-weight: 500;
}

.diagram-arrow {
  font-size: 2rem;
  color: var(--proctor-primary);
  transform: rotate(-45deg);
}

.guide-text {
  padding: 16px 24px;
  text-align: center;
  background: var(--bg-primary);
}

.guide-highlight {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--proctor-primary);
  margin: 0 0 4px 0;
}

.guide-detail {
  font-size: 0.9rem;
  color: var(--text-tertiary);
  margin: 0;
}

.setup-steps {
  padding: 0 24px 20px;
}

.step-item {
  display: flex;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid var(--border-light);
}

.step-item:last-child {
  border-bottom: none;
}

.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--proctor-primary), var(--proctor-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
  margin-top: 4px;
}

.step-content {
  flex: 1;
  min-width: 0;
}

.step-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.step-desc {
  font-size: 0.85rem;
  color: var(--text-tertiary);
  line-height: 1.5;
}

/* 确认和按钮 */
.action-card {
  padding: 24px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 12px 0;
}

.checkbox-input {
  display: none;
}

.checkmark {
  width: 22px;
  height: 22px;
  border: 2px solid var(--border-medium);
  border-radius: 6px;
  position: relative;
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.2s;
  background: var(--bg-primary);
}

.checkbox-input:checked + .checkmark {
  background: linear-gradient(135deg, var(--proctor-primary), var(--proctor-secondary));
  border-color: transparent;
}

.checkbox-input:checked + .checkmark::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.checkbox-text {
  flex: 1;
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
}

.start-button {
  width: 100%;
  padding: 18px 24px;
  background: linear-gradient(135deg, var(--proctor-primary) 0%, var(--proctor-secondary) 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
}

.start-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.start-button:disabled {
  background: var(--border-medium);
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
  box-shadow: none;
}

.button-text {
  letter-spacing: 0.5px;
}

.button-icon {
  font-size: 1.1rem;
  transition: transform 0.2s;
}

.start-button:hover:not(:disabled) .button-icon {
  transform: translateX(4px);
}
</style>
